import { isNameLengthValid } from 'dash-common';
import {
  Form,
  FormContext,
  FormErrorTypes,
  Input,
  Modal,
  ModalContext,
} from 'dash-ui';
import {
  ChangeEvent,
  FormEvent,
  useContext,
  useEffect,
  useMemo,
  useRef,
} from 'react';
import { useTranslation } from 'react-i18next';

import {
  GetMarketAreasDocument,
  useCreateMarketAreaMutation,
  useEditMarketAreaMutation,
  useGetUsStatesQuery,
} from 'gql/generated.graphql';
import {
  FormFields,
  MarketAreaModals,
} from 'pages/market-areas/MarketAreasPage.constants';

export const SaveMarketAreaModal = () => {
  const { t } = useTranslation(['pages', 'common', 'forms']);
  const formRef = useRef<HTMLFormElement>(null);

  const {
    formState,
    stateHandlers,
    setFormValues,
    resetFormErrors,
    errorHandlers,
    formOnSubmit,
    formOnError,
    formErrors,
  } = useContext(FormContext);

  const { isOpenCurrent, toggleModal, payload } = useContext(ModalContext);
  const isOpen = isOpenCurrent[MarketAreaModals.SAVE_MARKET_AREA_MODAL];

  const { data: statesData } = useGetUsStatesQuery();

  const statesOptions = useMemo(() => {
    const states = statesData?.getUSStates || [];
    return states.map(({ _id, name }) => ({
      value: _id,
      label: name,
    }));
  }, [statesData]);

  const [createMarketArea, { loading: createLoading }] =
    useCreateMarketAreaMutation({
      refetchQueries: [GetMarketAreasDocument],
    });

  const [editMarketArea, { loading: editLoading }] = useEditMarketAreaMutation({
    refetchQueries: [GetMarketAreasDocument],
  });

  useEffect(() => {
    if (payload?.mode === 'update') {
      setFormValues({
        name: payload?.marketArea?.name,
        state: payload?.marketArea?.state._id,
      });
    }
  }, [payload]);

  const handleSelectInputs = (fieldName: FormFields) => (value: string) => {
    stateHandlers[fieldName](value);
  };

  const validateName = (name: string) => {
    if (!name) {
      errorHandlers[FormFields.Name]({
        type: FormErrorTypes.Invalid,
        message: t('forms:messages.nameBlank'),
      });
      return false;
    }
    if (!isNameLengthValid(name)) {
      errorHandlers[FormFields.Name]({
        type: FormErrorTypes.Invalid,
        message: t('forms:messages.nameInvalid'),
      });
      return false;
    }
    resetFormErrors();
    return true;
  };

  const onTextChange = (e: ChangeEvent<HTMLInputElement>) => {
    setFormValues({ name: e.target.value });
    validateName(e.target.value);
  };

  const onCloseModal = () => {
    setFormValues({ name: null });
    resetFormErrors();
    toggleModal(false, MarketAreaModals.SAVE_MARKET_AREA_MODAL);
  };

  const onSubmit = (e: FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    e.stopPropagation();
    formOnSubmit();
    const { name, state } = formState;

    if (![validateName(name), validateName(state)].every(Boolean)) {
      return null;
    }
    const isCreation = payload?.mode === 'add';
    const mutation = isCreation ? createMarketArea : editMarketArea;

    mutation({
      variables: {
        input: {
          ...(payload?.marketArea?._id && { _id: payload?.marketArea?._id }),
          name,
          stateId: state,
        },
      },
      onCompleted: onCloseModal,
      onError: (error) => {
        formOnError(
          t(`common:serverMessages.${error.message}`) || error.message
        );
      },
    });
  };

  const isLoading = createLoading || editLoading;

  return (
    <Modal
      isOpen={isOpen}
      onRequestClose={onCloseModal}
      title={String(
        payload?.mode === 'update'
          ? t(`pages:marketAreasPage.updateMarketAreaModalLabel`)
          : t('pages:marketAreasPage.addMarketAreaModalLabel')
      )}
      cancelButton={{ title: t('common:labels.cancel'), onClick: onCloseModal }}
      submitButton={{
        title: t(`common:labels.${payload?.mode}`),
        isLoading,
        disabled: isLoading,
        onClick: () => {
          if (formRef.current) {
            formRef.current.requestSubmit();
          }
        },
      }}
    >
      <Form ref={formRef} onSubmit={onSubmit}>
        <Form.Group mt={2}>
          <Form.Label required>{t('forms:labels.name')}</Form.Label>
          <Input
            w="100%"
            placeholder={t('forms:placeholders.marketAreaName') || ''}
            onChange={onTextChange}
            value={formState.name || ''}
            $errorType={formErrors[FormFields.Name]?.type}
          />
          <Form.Feedback $errorType={formErrors[FormFields.Name]?.type}>
            {formErrors[FormFields.Name]?.message}
          </Form.Feedback>
        </Form.Group>
        <Form.Group w="100%" mt={3}>
          <Form.Label required>{t('forms:labels.state')}</Form.Label>
          <Form.Select
            options={statesOptions.map((item) => ({
              value: item.value,
              label: item.label,
            }))}
            value={formState[FormFields.State]}
            isLoading={isLoading}
            menuPosition="fixed"
            placeholder={t('forms:placeholders.selectOne')}
            w="100%"
            onChanged={handleSelectInputs(FormFields.State)}
            $errorType={formErrors[FormFields.State]?.type}
          />
          <Form.Feedback $errorType={formErrors[FormFields.State]?.type}>
            {formErrors[FormFields.State]?.message}
          </Form.Feedback>
        </Form.Group>
      </Form>
    </Modal>
  );
};

export default SaveMarketAreaModal;
